import { computed, reactive, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import type { JSONContent } from '@tiptap/vue-3'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { useSnackStore } from '@/stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'

export enum SurveyRecurrenceFrequency {
  Week = 'week',
  Month = 'month',
}

export const useStartSurvey = (isOpen: Ref<boolean>, onCreated?: (surveyId: string) => void) => {
  const START_SURVEY_STEPS = {
    selectMembersAndTemplate: 'select-members-and-template',
    selectQuestionGroups: 'select-question-groups',
    addMetadata: 'add-metadata',
  } as const
  type StartSurveyStep = (typeof START_SURVEY_STEPS)[keyof typeof START_SURVEY_STEPS]

  const { t } = useI18n()
  const snackStore = useSnackStore()

  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus: templateLoadingStatus } = storeToRefs(feedbackTopicStore)

  const surveysUpdatedBus = useEventBus(EventKey.surveyUpdated)

  const surveyData = reactive<StartSurveyFormData>({
    title: undefined,
    message: undefined,
    feedbackTopicId: undefined,
    feedbackSenderIds: [],
    isRecurring: false,
    recurrenceFrequency: SurveyRecurrenceFrequency.Week,
    recurrenceInterval: 1,
    nextOccurrenceAt: new Date(),
    questionGroupIds: [],
  })

  const errors = ref<ErrorMap>(new Map())
  const stepIndex = ref(0)

  const filteredTemplates = computed(() =>
    templates.value.filter((template) => template.type === FeedbackTopicType.Journal),
  )

  const configurableTemplates = computed(() => {
    const selectedTemplate = filteredTemplates.value.find(
      (template) => template.id === surveyData.feedbackTopicId,
    )

    if (
      selectedTemplate &&
      selectedTemplate.questionGroupSections &&
      selectedTemplate.questionGroupSections.filter((section) => !section.required).length > 0
    ) {
      return [selectedTemplate]
    }
    return []
  })

  const isConfigurable = computed(() => configurableTemplates.value.length > 0)

  const steps = computed<StartSurveyStep[]>(() => [
    START_SURVEY_STEPS.selectMembersAndTemplate,
    ...(isConfigurable.value ? [START_SURVEY_STEPS.selectQuestionGroups] : []),
    START_SURVEY_STEPS.addMetadata,
  ])

  const step = computed(() => steps.value[stepIndex.value])
  const isFirstStep = computed(() => stepIndex.value === 0)
  const isLastStep = computed(() => stepIndex.value === steps.value.length - 1)

  const validateStep = () => {
    errors.value.clear()

    switch (step.value) {
      case START_SURVEY_STEPS.selectMembersAndTemplate:
        if (surveyData.feedbackSenderIds.length === 0) {
          errors.value.set('feedbackSenderIds', t('feedback.formValidation.members'))
        }
        if (!surveyData.feedbackTopicId) {
          errors.value.set('feedbackTopicId', t('feedback.formValidation.templates'))
        }
        break
      case START_SURVEY_STEPS.selectQuestionGroups:
        if (
          surveyData.questionGroupIds.length === 0 &&
          !configurableTemplates.value.some((template) =>
            template.questionGroupSections?.some((section) => section.required),
          )
        ) {
          errors.value.set('questionGroupIds', t('feedback.formValidation.questionGroups'))
        }
        break
      case START_SURVEY_STEPS.addMetadata:
        if (!surveyData.nextOccurrenceAt) {
          errors.value.set('nextOccurrenceAt', t('feedback.formValidation.nextOccurrenceAt'))
        }
        if (surveyData.isRecurring) {
          if (
            !Number.isInteger(surveyData.recurrenceInterval) ||
            Number(surveyData.recurrenceInterval) < 1
          ) {
            errors.value.set('recurrenceInterval', t('feedback.formValidation.recurrenceInterval'))
          }

          if (
            surveyData.recurrenceFrequency === SurveyRecurrenceFrequency.Week &&
            ![1, 2, 4].includes(Number(surveyData.recurrenceInterval))
          ) {
            errors.value.set(
              'recurrenceInterval',
              t('feedback.formValidation.recurrenceWeeklyInterval'),
            )
          }
        }
        break
    }
  }

  const nextStep = () => {
    validateStep()
    if (errors.value.size === 0 && !isLastStep.value) {
      stepIndex.value++
    }
  }

  const previousStep = () => {
    if (stepIndex.value > 0) {
      stepIndex.value--
    }
    errors.value.clear()
  }

  const clearForm = () => {
    errors.value.clear()
    surveyData.title = undefined
    surveyData.message = undefined
    surveyData.feedbackTopicId = undefined
    surveyData.feedbackSenderIds = []
    surveyData.isRecurring = false
    surveyData.recurrenceFrequency = SurveyRecurrenceFrequency.Week
    surveyData.recurrenceInterval = 1
    surveyData.nextOccurrenceAt = new Date()
    surveyData.questionGroupIds = []
    stepIndex.value = 0
  }

  watch(
    surveyData,
    () => {
      errors.value.clear()
    },
    { deep: true },
  )

  watch(isOpen, (newValue) => {
    if (!newValue) {
      clearForm()
    }
  })

  const getTemplateQuestionGroupIds = (templateId?: string) => {
    if (!templateId) return []

    return (
      filteredTemplates.value
        .find((template) => template.id === templateId)
        ?.questionGroupSections?.flatMap((section) => section.questionGroups)
        .map((questionGroup) => questionGroup.id) ?? []
    )
  }

  watch(
    () => surveyData.feedbackTopicId,
    (templateId, previousTemplateId) => {
      const addedQuestionGroupIds = getTemplateQuestionGroupIds(templateId)
      surveyData.questionGroupIds = Array.from(
        new Set([...surveyData.questionGroupIds, ...addedQuestionGroupIds]),
      )

      const removedQuestionGroupIds = new Set(getTemplateQuestionGroupIds(previousTemplateId))
      surveyData.questionGroupIds = surveyData.questionGroupIds.filter(
        (id) => !removedQuestionGroupIds.has(id),
      )
    },
  )

  watch(steps, (newSteps) => {
    if (stepIndex.value >= newSteps.length) {
      stepIndex.value = Math.max(0, newSteps.length - 1)
    }
  })

  const {
    mutate: createSurvey,
    status: createSurveyStatus,
    data: createdSurveyId,
  } = usePost<string, CreateCoachSurveyPayload>('coaching/survey')

  const saveSurvey = async () => {
    validateStep()
    if (errors.value.size > 0) {
      return
    }

    if (!surveyData.nextOccurrenceAt) {
      return
    }

    const payload: CreateCoachSurveyPayload = {
      title: surveyData.title,
      message: surveyData.message,
      feedbackTopicId: surveyData.feedbackTopicId,
      feedbackSenderIds: surveyData.feedbackSenderIds,
      questionGroupIds: surveyData.questionGroupIds,
      nextOccurrenceAt: serializeNextOccurrenceAt(surveyData.nextOccurrenceAt),
      isRecurring: surveyData.isRecurring,
      recurrenceFrequency: surveyData.isRecurring ? surveyData.recurrenceFrequency : undefined,
      recurrenceInterval: surveyData.isRecurring ? surveyData.recurrenceInterval : undefined,
    }

    await createSurvey(payload)

    switch (createSurveyStatus.value) {
      case ApiRequestStatus.Success:
        if (onCreated) {
          onCreated(createdSurveyId.value)
        }

        snackStore.showInfo(t('feedback.requestFeedbackSuccess'))
        surveysUpdatedBus.emit({ surveyId: createdSurveyId.value })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.requestFeedbackError'))
        break
    }
  }

  return {
    step,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    saveSurvey,
    surveyData,
    errors,
    filteredTemplates,
    clearForm,
    templateLoadingStatus,
    configurableTemplates,
  }
}

export type StartSurveyFormData = {
  title?: string
  message?: JSONContent
  feedbackTopicId?: string
  feedbackSenderIds: string[]
  questionGroupIds: string[]
  isRecurring: boolean
  recurrenceFrequency?: SurveyRecurrenceFrequency
  recurrenceInterval?: number
  nextOccurrenceAt?: Date
}

export type ErrorMap = Map<keyof StartSurveyFormData, string>

type CreateCoachSurveyPayload = Omit<StartSurveyFormData, 'nextOccurrenceAt'> & {
  nextOccurrenceAt: string
}

export const serializeNextOccurrenceAt = (
  date: Date,
  options?: { mustBeFuture?: boolean },
): string => {
  const result = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  )

  const hasTime =
    date.getHours() !== 0 ||
    date.getMinutes() !== 0 ||
    date.getSeconds() !== 0 ||
    date.getMilliseconds() !== 0

  if (!hasTime) {
    const now = new Date()
    result.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())
  }

  if (options?.mustBeFuture && result.getTime() <= Date.now()) {
    result.setTime(Date.now() + 60_000)
  }

  return result.toISOString()
}
